import {
  APIExtractionDraft,
  APIExtractionDraftEntitiesResponse,
  APIExtractionDraftEntityCountsResponse,
  APIExtractionDraftEntityTypesResponse,
  APIExtractionDraftGroupTotalsResponse,
  APIExtractionDraftReferenceTargetCandidatesResponse,
  ApproveExtractionDraftWorkspaceRequest,
  ApproveExtractionDraftWorkspaceResponse,
  ArdoqId,
  CreateExtractionDraftRequest,
  FetchExtractionDraftEntitiesRequest,
  FetchExtractionDraftEntityGroupTotalsRequest,
  FetchExtractionDraftEntityTypesRequest,
  FetchExtractionDraftReferenceTargetCandidatesRequest,
  MergeExtractionDraftRequest,
  MergeExtractionDraftResult,
  SearchExtractionDraftEntitiesRequest,
  UnapproveExtractionDraftWorkspaceRequest,
} from '@ardoq/api-types';
import {
  Method,
  deleteEntity,
  fetchEntities,
  fetchEntity,
  fetchJson,
  fetchVoid,
  renameEntity,
  toRequestOptions,
} from './api';
import { ApiResponse } from './types';

const create = ({
  name,
  workspaceIds,
}: CreateExtractionDraftRequest): ApiResponse<APIExtractionDraft> => {
  const request = new Request(
    '/api/extraction-draft',
    toRequestOptions(Method.POST, { name, workspaceIds })
  );
  return fetchJson<APIExtractionDraft>(request);
};

const fetchAll = fetchEntities<APIExtractionDraft>('extraction-draft');

const fetchOne = fetchEntity<APIExtractionDraft>('extraction-draft');

const deleteOne = deleteEntity('extraction-draft');

const rename = renameEntity<APIExtractionDraft>('extraction-draft');

const fetchDraftEntities = ({
  extractionDraftId,
  workspaceId,
  offset,
  grouping,
  typeIds,
}: FetchExtractionDraftEntitiesRequest): ApiResponse<APIExtractionDraftEntitiesResponse> => {
  const params = new URLSearchParams({ offset: String(offset) });
  if (workspaceId) {
    params.set('workspaceId', workspaceId);
  }
  if (grouping) {
    params.set('grouping', grouping);
  }
  if (typeIds?.length) {
    params.set('typeIds', typeIds.join(','));
  }
  const request = new Request(
    `/api/extraction-draft/${extractionDraftId}/entities?${params.toString()}`,
    toRequestOptions(Method.GET, null)
  );
  return fetchJson<APIExtractionDraftEntitiesResponse>(request);
};

const fetchDraftEntityTypes = ({
  extractionDraftId,
  workspaceId,
}: FetchExtractionDraftEntityTypesRequest): ApiResponse<APIExtractionDraftEntityTypesResponse> => {
  const params = new URLSearchParams();
  if (workspaceId) {
    params.set('workspaceId', workspaceId);
  }
  const query = params.toString();
  const request = new Request(
    `/api/extraction-draft/${extractionDraftId}/entities/types${query ? `?${query}` : ''}`,
    toRequestOptions(Method.GET, null)
  );
  return fetchJson<APIExtractionDraftEntityTypesResponse>(request);
};

const fetchDraftEntityCounts = (
  extractionDraftId: ArdoqId
): ApiResponse<APIExtractionDraftEntityCountsResponse> => {
  const request = new Request(
    `/api/extraction-draft/${extractionDraftId}/entities/counts`,
    toRequestOptions(Method.GET, null)
  );
  return fetchJson<APIExtractionDraftEntityCountsResponse>(request);
};

const fetchDraftEntityGroupTotals = ({
  extractionDraftId,
  workspaceId,
  grouping,
}: FetchExtractionDraftEntityGroupTotalsRequest): ApiResponse<APIExtractionDraftGroupTotalsResponse> => {
  const params = new URLSearchParams({ workspaceId, grouping });
  const request = new Request(
    `/api/extraction-draft/${extractionDraftId}/entities/group-totals?${params.toString()}`,
    toRequestOptions(Method.GET, null)
  );
  return fetchJson<APIExtractionDraftGroupTotalsResponse>(request);
};

const searchDraftEntities = ({
  extractionDraftId,
  workspaceId,
  searchTerm,
  offset,
  grouping,
  typeIds,
}: SearchExtractionDraftEntitiesRequest): ApiResponse<APIExtractionDraftEntitiesResponse> => {
  const params = new URLSearchParams({ q: searchTerm, offset: String(offset) });
  if (workspaceId) {
    params.set('workspaceId', workspaceId);
  }
  if (grouping) {
    params.set('grouping', grouping);
  }
  if (typeIds?.length) {
    params.set('typeIds', typeIds.join(','));
  }
  const request = new Request(
    `/api/extraction-draft/${extractionDraftId}/entities/search?${params.toString()}`,
    toRequestOptions(Method.GET, null)
  );
  return fetchJson<APIExtractionDraftEntitiesResponse>(request);
};

/**
 * The components a reference of `referenceTypeId` out of `sourceId` may point at: the
 * draft's own components and the ones its workspaces already hold, narrowed to the
 * component types the metamodel allows between the two. Capped server-side, with no
 * offset — `hasMore` means narrow the search, not fetch a next page.
 */
const fetchReferenceTargetCandidates = ({
  extractionDraftId,
  sourceId,
  referenceTypeId,
  workspaceId,
  searchTerm,
}: FetchExtractionDraftReferenceTargetCandidatesRequest): ApiResponse<APIExtractionDraftReferenceTargetCandidatesResponse> => {
  const params = new URLSearchParams({ sourceId, referenceTypeId });
  if (workspaceId) {
    params.set('workspaceId', workspaceId);
  }
  if (searchTerm) {
    params.set('q', searchTerm);
  }
  const request = new Request(
    `/api/extraction-draft/${extractionDraftId}/references/target-candidates?${params.toString()}`,
    toRequestOptions(Method.GET, null)
  );
  return fetchJson<APIExtractionDraftReferenceTargetCandidatesResponse>(
    request
  );
};

const merge = ({
  extractionDraftId,
}: MergeExtractionDraftRequest): ApiResponse<MergeExtractionDraftResult> => {
  const request = new Request(
    `/api/extraction-draft/${extractionDraftId}/merge`,
    toRequestOptions(Method.POST, null)
  );
  return fetchJson<MergeExtractionDraftResult>(request);
};

const discardBranch = (extractionDraftId: ArdoqId): ApiResponse<void> => {
  const request = new Request(
    `/api/extraction-draft/${extractionDraftId}/branch`,
    toRequestOptions(Method.DELETE, null)
  );
  return fetchVoid(request);
};

const approveWorkspace = ({
  extractionDraftId,
  workspaceId,
  approvalDefault,
  componentIds,
}: ApproveExtractionDraftWorkspaceRequest): ApiResponse<ApproveExtractionDraftWorkspaceResponse> => {
  const request = new Request(
    `/api/extraction-draft/${extractionDraftId}/workspaces/${workspaceId}/approval`,
    toRequestOptions(Method.POST, { approvalDefault, componentIds })
  );
  return fetchJson<ApproveExtractionDraftWorkspaceResponse>(request);
};

const unapproveWorkspace = ({
  extractionDraftId,
  workspaceId,
}: UnapproveExtractionDraftWorkspaceRequest): ApiResponse<void> => {
  const request = new Request(
    `/api/extraction-draft/${extractionDraftId}/workspaces/${workspaceId}/approval`,
    toRequestOptions(Method.DELETE, null)
  );
  return fetchVoid(request);
};

export const extractionDraftApi = {
  create,
  fetchAll,
  fetchOne,
  fetchEntities: fetchDraftEntities,
  fetchEntityTypes: fetchDraftEntityTypes,
  fetchEntityCounts: fetchDraftEntityCounts,
  fetchEntityGroupTotals: fetchDraftEntityGroupTotals,
  searchEntities: searchDraftEntities,
  fetchReferenceTargetCandidates,
  deleteOne,
  discardBranch,
  approveWorkspace,
  unapproveWorkspace,
  merge,
  rename,
};
